import { useEffect, useId, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import { useAuth } from '../../../core/authContext';
import { requestSisConnection } from '../../../core/setupStateApi';
import { feedbackSurveyUrl } from '../../../core/surveyLink';
import { fetchGenerationSettings } from '../../../core/thresholdsApi';
import type { Thresholds } from '../../../core/thresholds';
import { fetchUploads } from '../../Data/uploadsApi';
import { isWidaKind, type UploadedFile } from '../../Data/uploadedFiles';
import { fetchExitCriteria, type ExitCriteria } from '../../Generate/confirmedSettings';
import { NAV_ROUTES } from '../../Home/navRoutes';
import { serverSourcedSubmission } from '../../ProgramEvaluation/dataStatus';
import { useProgramEvaluationGeneration } from '../../ProgramEvaluation/generation-hook';
import { OUTLINED_ACTION_SX } from '../../Reports/components/ReportCard';
import { programEvaluationCardView, type PeCardView } from '../../Reports/programEvaluationCard';
import { accessStudentCount } from '../../Roster/bannerState';
import { latestUploadDate } from '../../Roster/lastUpdated';
import { fetchRosterClassification, type RosterClassificationRow } from '../../Roster/rosterClassification';
import { classificationToRosterRow } from '../../Roster/rosterRows';
import { GET_STARTED_URL } from '../../Samples/sampleCopy';
import { PRIMARY_BUTTON_SX, TEXT_BUTTON_SX } from '../../Samples/SampleBanner';
import { SETTINGS_EXIT_CRITERIA_ROUTE, SETTINGS_YEARS_ROUTE } from '../../Settings/routes';
import {
  checklistDoneCount,
  checklistIntro,
  checklistNext,
  checklistSteps,
  stepCount,
  type ChecklistDetails,
  type ChecklistStep,
} from '../setupChecklist';
import { programEvaluationReady, setupDone, type SetupFacts, type SetupStatus } from '../setupStatus';
import { DASH } from '../tokens';

export const CHECKLIST = {
  bg: '#EFEFFA',
  segmentTodo: '#C1C1CD',
  segmentWait: '#A9A9E5',
  segmentDone: '#52BB3A',
  iconTodo: '#9E9EB2',
  iconWait: DASH.ghost,
  iconDone: '#52BB3A',
  error: '#B20101',
  divider: 'rgba(8, 8, 27, 0.15)',
} as const;

type CriteriaDetail = Pick<ExitCriteria, 'stateName' | 'source'>;

type Props = {
  status: SetupStatus;
  onGeneratePlans: (count: number) => void;
  flashlightConnected?: boolean | null;
  uploadsSource?: () => Promise<UploadedFile[]>;
  classificationSource?: () => Promise<RosterClassificationRow[] | null>;
  criteriaSource?: () => Promise<CriteriaDetail>;
  yearsSource?: () => Promise<Thresholds>;
};

type Count = { kind: 'pending' } | { kind: 'known'; value: number } | { kind: 'failed' };

const BUTTON_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 36,
  padding: '0 16px',
  borderRadius: '8px',
  fontFamily: 'inherit',
  fontSize: 16,
  lineHeight: '20px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  textDecoration: 'none',
} as const;

const PRIMARY_SX = { ...PRIMARY_BUTTON_SX, ...BUTTON_SX } as const;
const OUTLINED_SX = {
  ...OUTLINED_ACTION_SX,
  ...BUTTON_SX,
  '&:disabled': { cursor: 'default', color: DASH.lockedText, bgcolor: '#FFFFFF' },
} as const;

function useDetail<T>(enabled: boolean, read: () => Promise<T>, label: string): T | null | undefined {
  const [state, setState] = useState<{ read: () => Promise<T>; value: T | null } | null>(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    read()
      .then((value) => {
        if (active) setState({ read, value });
      })
      .catch((err: unknown) => {
        console.warn(`setup checklist: ${label} unavailable`, err);
        if (active) setState({ read, value: null });
      });
    return () => {
      active = false;
    };
  }, [enabled, read, label]);
  if (!enabled) return null;
  return state === null || state.read !== read ? undefined : state.value;
}

function StepIcon({ step, size = 24 }: { step: ChecklistStep; size?: number }) {
  const sx = { width: size, height: size, flexShrink: 0 };
  switch (step.icon) {
    case 'done':
      return <CheckCircleIcon data-testid="step-icon-done" sx={{ ...sx, color: CHECKLIST.iconDone }} />;
    case 'wait':
      return <AccessTimeIcon data-testid="step-icon-wait" sx={{ ...sx, color: CHECKLIST.iconWait }} />;
    case 'error':
      return <ErrorOutlineIcon data-testid="step-icon-error" sx={{ ...sx, color: CHECKLIST.error }} />;
    default:
      return <RadioButtonUncheckedIcon data-testid="step-icon-todo" sx={{ ...sx, color: CHECKLIST.iconTodo }} />;
  }
}

function Subtitle({ step, size = 12 }: { step: ChecklistStep; size?: number }) {
  return (
    <Typography
      component="span"
      sx={{ fontSize: size, lineHeight: size === 12 ? '16px' : '20px', fontWeight: 500, color: step.icon === 'error' ? CHECKLIST.error : DASH.muted }}
    >
      {step.subtitle}
    </Typography>
  );
}

type Actions = {
  onGetStarted: () => void;
  onUpload: () => void;
  onGeneratePlans: () => void;
  plansCountPending: boolean;
  onGenerateEvaluation: () => void;
  onRetryEvaluation: () => void;
};



function StepAction({ step, actions }: { step: ChecklistStep; actions: Actions }) {
  if (step.action === null || step.state !== 'todo') return null;
  const sx = step.primary ? PRIMARY_SX : OUTLINED_SX;
  switch (step.action) {
    case 'getStarted':
      return (
        <Box component="a" href={GET_STARTED_URL} target="_blank" rel="noopener noreferrer" onClick={actions.onGetStarted} sx={sx}>
          Get Started
        </Box>
      );
    case 'upload':
      return (
        <Box component="button" type="button" onClick={actions.onUpload} sx={sx}>
          Upload
        </Box>
      );
    case 'reviewCriteria':
    case 'reviewYears':
      return (
        <Box component={RouterLink} to={step.action === 'reviewCriteria' ? SETTINGS_EXIT_CRITERIA_ROUTE : SETTINGS_YEARS_ROUTE} sx={sx}>
          Review
        </Box>
      );
    case 'generatePlans':
      return (
        <Box component="button" type="button" disabled={step.locked || actions.plansCountPending} onClick={actions.onGeneratePlans} sx={sx}>
          Generate
        </Box>
      );
    case 'generateEvaluation':
      return (
        <Box component="button" type="button" disabled={step.locked} onClick={actions.onGenerateEvaluation} sx={sx}>
          Generate
        </Box>
      );
    default:
      return (
        <Box component="button" type="button" onClick={actions.onRetryEvaluation} sx={sx}>
          Try again
        </Box>
      );
  }
}

function Strip({ children, labelledBy }: { children: React.ReactNode; labelledBy: string }) {
  return (
    <Box
      component="section"
      aria-labelledby={labelledBy}
      data-testid="setup-checklist"
      sx={{ display: 'flex', flexDirection: 'column', bgcolor: CHECKLIST.bg, borderRadius: '12px' }}
    >
      {children}
    </Box>
  );
}

function Heading({ id }: { id: string }) {
  return (
    <Typography id={id} component="h2" sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 700, color: DASH.ink, textWrap: 'balance' }}>
      Finish setting up
    </Typography>
  );
}

function FailedStrip({ titleId, onRetry }: { titleId: string; onRetry: () => void }) {
  return (
    <Strip labelledBy={titleId}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '24px', padding: '16px 16px 16px 24px' }}>
        <Box sx={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Heading id={titleId} />
          <Typography sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 500, color: DASH.muted }}>
            Couldn’t load your setup steps right now.
          </Typography>
        </Box>
        <Box component="button" type="button" onClick={onRetry} sx={OUTLINED_SX}>
          Retry
        </Box>
      </Box>
    </Strip>
  );
}

type Sources = Required<Omit<Props, 'status' | 'flashlightConnected' | 'onGeneratePlans'>>;

function ChecklistBody({
  status,
  facts,
  sources,
  flashlightConnected,
  onGeneratePlans,
}: {
  status: SetupStatus;
  facts: SetupFacts;
  sources: Sources;
  flashlightConnected: boolean | null;
  onGeneratePlans: (count: number) => void;
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const titleId = useId();
  const stepsId = useId();
  const [open, setOpen] = useState(false);
  const peReady = programEvaluationReady(facts);
  const generation = useProgramEvaluationGeneration({
    inputsProvider: serverSourcedSubmission,
    enabled: peReady && facts.programEvaluation !== null,
    schoolYear: facts.programEvaluation?.schoolYear ?? facts.schoolYear ?? undefined,
    unitMode: facts.programEvaluation?.unitMode ?? undefined,
    onReady: status.refetchProgramEvaluation,
  });
  const { refetchProgramEvaluation } = status;
  const runStarted = generation.phase === 'generating';
  const pollGaveUp = generation.pollExhausted;
  useEffect(() => {
    if (peReady && (runStarted || pollGaveUp)) refetchProgramEvaluation();
  }, [peReady, runStarted, pollGaveUp, refetchProgramEvaluation]);

  const uploads = useDetail(facts.accessUploaded, sources.uploadsSource, 'uploads');
  const classification = useDetail(facts.accessUploaded, sources.classificationSource, 'roster');
  const criteria = useDetail(true, sources.criteriaSource, 'exit criteria');
  const years = useDetail(true, sources.yearsSource, 'generation settings');

  const count: Count =
    classification === undefined ? { kind: 'pending' } : classification === null ? { kind: 'failed' } : { kind: 'known', value: accessStudentCount(classification.map(classificationToRosterRow)) };
  const details: ChecklistDetails = {
    accessUploadedAt: uploads ? latestUploadDate(uploads.filter((f) => isWidaKind(f.kind))) : null,
    accessStudentCount: count.kind === 'known' ? count.value : null,
    criteria: criteria ? { stateName: criteria.stateName, source: criteria.source } : null,
    years: years ?? null,
  };
  const evaluation: PeCardView = programEvaluationCardView(
    peReady ? 'present' : 'absent',
    facts.programEvaluation,
    generation,
    status.programEvaluationGivenUp,
    facts.schoolYear,
  );
  const steps = checklistSteps(facts, details, evaluation);
  const next = checklistNext(steps);
  const done = checklistDoneCount(steps);
  const retryEvaluation = () => {
    const failedPhase = generation.phase === 'failed' || generation.phase === 'error';
    if (!failedPhase) {
      generation.regenerate();
      return;
    }
    const resumedFailure = generation.phase === 'failed' && !generation.freshJob;
    const freshnessUnknown =
      facts.programEvaluation === null ||
      facts.programEvaluation.stale ||
      facts.programEvaluation.status === 'none' ||
      generation.jobUpdatedAt === null;
    if (resumedFailure && freshnessUnknown) {
      generation.regenerate();
      return;
    }
    generation.retry();
  };
  const actions: Actions = {
    onGetStarted: () => {
      requestSisConnection()
        .then(() => status.refetch())
        .catch((err: unknown) => console.warn('setup checklist: SIS request not recorded', err));
    },
    onUpload: () => navigate(NAV_ROUTES.data),
    onGeneratePlans: () => {
      if (count.kind === 'known') onGeneratePlans(count.value);
      else navigate(NAV_ROUTES.roster);
    },
    plansCountPending: count.kind === 'pending',
    onGenerateEvaluation: generation.submit,
    onRetryEvaluation: retryEvaluation,
  };

  return (
    <Strip labelledBy={titleId}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '24px', padding: '16px 16px 16px 24px' }}>
        <Box sx={{ width: 240, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Heading id={titleId} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Box role="img" aria-label={`${done} of ${stepCount(facts)} steps done`} sx={{ flex: '1 1 0', display: 'flex', gap: '4px' }}>
              {steps.map((step) => (
                <Box
                  key={step.id}
                  data-state={step.state}
                  sx={{
                    flex: '1 1 0',
                    height: 4,
                    borderRadius: '9999px',
                    bgcolor: step.state === 'done' ? CHECKLIST.segmentDone : step.state === 'wait' ? CHECKLIST.segmentWait : CHECKLIST.segmentTodo,
                  }}
                />
              ))}
            </Box>
            <Typography component="small" sx={{ fontSize: 12, lineHeight: '16px', color: DASH.muted, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
              {done} of {stepCount(facts)} done
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            flex: '1 1 0',
            minWidth: 0,
            alignSelf: 'stretch',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            paddingLeft: '24px',
            borderLeft: open ? 0 : `1px solid ${CHECKLIST.divider}`,
          }}
        >
          {!open && (
            <>
              <StepIcon step={next} />
              <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <Typography component="small" sx={{ fontSize: 12, lineHeight: '16px', color: DASH.muted }}>
                  Next
                </Typography>
                <Typography component="b" sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 700, color: DASH.ink }}>
                  {next.title}
                </Typography>
                <Subtitle step={next} />
              </Box>
            </>
          )}
        </Box>
        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          {!open && <StepAction step={next} actions={actions} />}
          <Box
            component="button"
            type="button"
            aria-expanded={open}
            aria-controls={stepsId}
            onClick={() => setOpen((o) => !o)}
            sx={{ ...TEXT_BUTTON_SX, border: 0, cursor: 'pointer', gap: '4px' }}
          >
            {open ? 'Hide steps' : 'All steps'}
            <ExpandMoreIcon sx={{ width: 20, height: 20, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease-out' }} />
          </Box>
        </Box>
      </Box>
      {open && (
        <Box id={stepsId} sx={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '0 24px 8px' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '24px', paddingTop: '12px', borderTop: `1px solid ${CHECKLIST.divider}` }}>
            <Typography sx={{ maxWidth: 720, fontSize: 16, lineHeight: '24px', fontWeight: 500, color: DASH.muted }}>
              {checklistIntro(facts, evaluation, flashlightConnected)}
            </Typography>
            <Box
              component="a"
              href={feedbackSurveyUrl(user?.id)}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ ...TEXT_BUTTON_SX, flexShrink: 0, height: 'auto', padding: '4px 8px' }}
            >
              Send feedback
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {steps.map((step, index) => (
              <Box
                key={step.id}
                data-testid="setup-step"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '12px 0',
                  borderTop: index === 0 ? 0 : `1px solid ${CHECKLIST.divider}`,
                }}
              >
                <StepIcon step={step} />
                <Box sx={{ width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
                  <Typography component="b" sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 700, color: DASH.ink }}>
                    {step.title}
                  </Typography>
                  <Subtitle step={step} />
                </Box>
                <Typography sx={{ flex: '1 1 0', minWidth: 0, fontSize: 16, lineHeight: '24px', fontWeight: 500, color: DASH.muted }}>
                  {step.description}
                </Typography>
                <Box sx={{ width: 160, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
                  <StepAction step={step} actions={actions} />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Strip>
  );
}

export function SetupChecklist({
  status,
  onGeneratePlans,
  flashlightConnected = null,
  uploadsSource = fetchUploads,
  classificationSource = fetchRosterClassification,
  criteriaSource = fetchExitCriteria,
  yearsSource = fetchGenerationSettings,
}: Props) {
  const titleId = useId();
  if (status.phase === 'failed') return <FailedStrip titleId={titleId} onRetry={status.refetch} />;
  if (status.phase !== 'ready' || status.facts === null || setupDone(status.facts)) return null;
  return (
    <ChecklistBody
      status={status}
      facts={status.facts}
      sources={{ uploadsSource, classificationSource, criteriaSource, yearsSource }}
      flashlightConnected={flashlightConnected}
      onGeneratePlans={onGeneratePlans}
    />
  );
}
